import { useEffect, useRef, useState } from "react";
import { register } from "../utils/audioBus";


const PREVIEW_VOLUME = 0.05;

/**
 * Spelar en 30-sekunders preview.
 *
 * `failed` säger att källan inte gick att spela. Det händer på riktigt:
 * previewlänkarna från Deezer är signerade och slutar gälla, och en låt
 * som legat i katalogen ett tag har ofta en död länk. Servern kan inte se
 * det utan att hämta filen, så det upptäcks först här — och då ska kortet
 * ta bort knappen i stället för att låta någon trycka på den igen.
 */
export function usePreviewAudio(url?: string) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!url) return;

    const audio = new Audio(url);
    audio.volume = PREVIEW_VOLUME;
    audio.preload = "none";

    const onError = () => {
      setFailed(true);
      setIsPlaying(false);
    };

    const onEnded = () => setIsPlaying(false);

    audio.addEventListener("error", onError);
    audio.addEventListener("ended", onEnded);

    audioRef.current = audio;
    setFailed(false);

    return () => {
      audio.removeEventListener("error", onError);
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.src = "";
      audioRef.current = null;
      setIsPlaying(false);
    };
  }, [url]);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    // Safari/iOS kan återställa volym vid play()
    audio.volume = PREVIEW_VOLUME;

    if (audio.paused) {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {
        // autoplay/user-gesture fail – ignorera tyst
      }
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  return { isPlaying, toggle, failed };
}
